import { AI_V2_LIMITS } from '../../supabase/functions/_shared/aiV2Protocol';
import { V2_POOL_AI_SPEED_KEY } from '../persistence/persistenceContract';

// How long 「AI 分析全部照片」 takes — said before it starts and counted down while it runs (Owner 2026-10-18:
// 「6分钟很久…别让用户以为是机子卡主」, then 「不要所有的预计都写6分钟，按真实预计时间来做」). Every estimate is
// worked out from the work THIS pool needs and from measured times, never a fixed figure:
//   AREA   one area-analysis request = 26 s + 1.7 s per photo: the least-squares line through 28 real area
//          analyses on 2026-10-18 (the 56 Helmig St photos, two retest runs, 1–14 photos per area, 9–61 s,
//          32.5 s on average; docs/qa/evidence/ai-retest-2026-10-18*/). The server reads an area's photos two
//          at a time, six calls at once, so most of the time is the model's own and a big area costs little
//          more than a small one.
//   SORT   one sort request = 7 s + 1.5 s per photo (12 photos ≈ 25 s): the 2026-10-16 run (56 photos, 5 sort
//          requests and 14 areas in about 6 minutes) less its area time.
//   PHONE  this phone's own measured speed: after every run watched in the foreground, the remembered factor
//          moves halfway toward what the run measured (its network, the provider's speed that day), so the
//          next estimate starts from this phone's real speed.
// While a run is going, every answered request also corrects the estimate of those still to come, and the
// areas are scheduled exactly as the run sends them: two at a time, the next one as soon as one finishes.

// Photos per sort request: one provider call answers well inside the function's time budget, and a
// 100-photo pool is a handful of calls.
export const POOL_SORT_BATCH = 12;
// Areas analyzed at the same time (each area call already analyzes its photos in parallel on the server).
export const POOL_AREA_CONCURRENCY = 2;

export const SORT_SECONDS_FIXED = 7;
export const SORT_SECONDS_PER_PHOTO = 1.5;
export const AREA_SECONDS_FIXED = 26;
export const AREA_SECONDS_PER_PHOTO = 1.7;
// Photos per area the sort ends up with on an ordinary house (56 photos → 14 areas on 2026-10-16).
const PHOTOS_PER_AREA_GUESS = 4;
// A remembered or measured speed outside this range is a phone that was asleep or a test, not a speed.
const SPEED_MIN = 1 / 3;
const SPEED_MAX = 3;
// An answer faster than this sent nothing (no readable photo): it says nothing about the speed.
const MIN_MEASURED_SECONDS = 1;

// 1 = as fast as measured on 2026-10-16 / 18; 2 = this phone has been taking twice as long.
export interface PoolAiSpeed {
  sort: number;
  area: number;
  // false = nothing measured on this phone yet (the figures above as they are).
  measured: boolean;
}

export const DEFAULT_POOL_AI_SPEED: PoolAiSpeed = Object.freeze({ sort: 1, area: 1, measured: false });

export interface PoolAiJobClock {
  areaId: string;
  // The photos this run sends for the area.
  photos: number;
  startedAt: number | null;
  finishedAt: number | null;
  // Analyzed (a failed or skipped area teaches nothing about the speed).
  ok: boolean;
}

// What a run records for its estimate.
export interface PoolAiClock {
  speed: PoolAiSpeed;
  // The inspection's enabled areas (how many the sort can fill).
  areasAvailable: number;
  // Photos in each sort request, in order, and the seconds each answered one took.
  sortBatches: number[];
  sortSeconds: number[];
  // When the sort request in flight was sent.
  sortSentAt: number;
  // The areas, in the order the run sends them (empty until the sort has answered).
  jobs: PoolAiJobClock[];
}

export interface PoolAiTiming {
  // 0–99 while it runs: time used over time used + time left, so it moves every second.
  percent: number;
  elapsedSeconds: number;
  // 0 = what is still running is taking longer than expected (the card says so instead of a time).
  remainingSeconds: number;
}

function clampSpeed(value: number): number {
  return Math.min(SPEED_MAX, Math.max(SPEED_MIN, value));
}

function sum(values: readonly number[]): number {
  return values.reduce((total, v) => total + v, 0);
}

export function sortRequestSeconds(photos: number): number {
  return photos <= 0 ? 0 : SORT_SECONDS_FIXED + SORT_SECONDS_PER_PHOTO * photos;
}

// One area: a request per AI_V2_LIMITS.maxPhotos photos, one after another.
export function areaSeconds(photos: number): number {
  let total = 0;
  for (let left = photos; left > 0; left -= AI_V2_LIMITS.maxPhotos) total += AREA_SECONDS_FIXED + AREA_SECONDS_PER_PHOTO * Math.min(left, AI_V2_LIMITS.maxPhotos);
  return total;
}

export function sortBatchesOf(photos: number): number[] {
  const batches: number[] = [];
  for (let left = photos; left > 0; left -= POOL_SORT_BATCH) batches.push(Math.min(left, POOL_SORT_BATCH));
  return batches;
}

// Before the sort has answered: the areas it is expected to fill and the photos of each.
export function guessAreaPhotos(photos: number, areasAvailable: number): number[] {
  if (photos <= 0) return [];
  const areas = Math.max(1, Math.min(Math.max(1, areasAvailable), Math.ceil(photos / PHOTOS_PER_AREA_GUESS)));
  return Array.from({ length: areas }, (_, i) => Math.floor(photos / areas) + (i < photos % areas ? 1 : 0));
}

// The areas as the run sends them: POOL_AREA_CONCURRENCY at a time, the next one as soon as one finishes.
// `running` = what the areas already started still need.
function scheduleSeconds(running: readonly number[], queued: readonly number[]): number {
  const workers = [...running];
  while (workers.length < POOL_AREA_CONCURRENCY) workers.push(0);
  for (const seconds of queued) {
    let at = 0;
    for (let i = 1; i < workers.length; i += 1) if (workers[i] < workers[at]) at = i;
    workers[at] += seconds;
  }
  return Math.max(0, ...workers);
}

// The speed of this run so far: the phone's remembered speed counts as one more answer next to this run's.
function runSpeed(remembered: number, actual: readonly number[], predicted: readonly number[]): number {
  const n = actual.length;
  const expected = sum(predicted);
  if (n === 0 || expected <= 0) return remembered;
  return clampSpeed((remembered + n * clampSpeed(sum(actual) / expected)) / (1 + n));
}

// Before anything is sent.
export function poolAiEstimateSeconds(photos: number, areasAvailable: number, speed: PoolAiSpeed = readPoolAiSpeed()): number {
  const sorting = sum(sortBatchesOf(photos).map(sortRequestSeconds)) * speed.sort;
  return sorting + scheduleSeconds([], guessAreaPhotos(photos, areasAvailable).map((p) => areaSeconds(p) * speed.area));
}

export function poolAiRemainingSeconds(run: { phase: 'sorting' | 'analyzing'; clock: PoolAiClock }, now: number): number {
  const { clock } = run;
  if (run.phase === 'sorting') {
    const answered = clock.sortSeconds.length;
    const speed = runSpeed(clock.speed.sort, clock.sortSeconds, clock.sortBatches.slice(0, answered).map(sortRequestSeconds));
    let remaining = 0;
    const inFlight = clock.sortBatches[answered];
    if (inFlight !== undefined) {
      remaining += Math.max(0, sortRequestSeconds(inFlight) * speed - Math.max(0, now - clock.sortSentAt) / 1000);
      remaining += sum(clock.sortBatches.slice(answered + 1).map(sortRequestSeconds)) * speed;
    }
    const areas = guessAreaPhotos(sum(clock.sortBatches), clock.areasAvailable);
    return remaining + scheduleSeconds([], areas.map((p) => areaSeconds(p) * clock.speed.area));
  }
  const done = clock.jobs.filter((j) => j.ok && j.startedAt !== null && j.finishedAt !== null);
  const speed = runSpeed(
    clock.speed.area,
    done.map((j) => ((j.finishedAt as number) - (j.startedAt as number)) / 1000),
    done.map((j) => areaSeconds(j.photos))
  );
  const running = clock.jobs.filter((j) => j.startedAt !== null && j.finishedAt === null).map((j) => Math.max(0, areaSeconds(j.photos) * speed - Math.max(0, now - (j.startedAt as number)) / 1000));
  const queued = clock.jobs.filter((j) => j.startedAt === null).map((j) => areaSeconds(j.photos) * speed);
  return scheduleSeconds(running, queued);
}

export function poolAiTiming(run: { phase: 'sorting' | 'analyzing'; startedAt: number; clock: PoolAiClock }, now: number): PoolAiTiming {
  const elapsed = Math.max(0, (now - run.startedAt) / 1000);
  const remaining = poolAiRemainingSeconds(run, now);
  // A few seconds of tail so the bar never reads 100% before the last answer is in.
  const percent = Math.min(99, Math.floor((100 * elapsed) / (elapsed + remaining + 3)));
  return { percent, elapsedSeconds: Math.floor(elapsed), remainingSeconds: Math.round(remaining) };
}

// ---------- this phone's speed ----------

export function readPoolAiSpeed(): PoolAiSpeed {
  try {
    if (typeof window === 'undefined') return DEFAULT_POOL_AI_SPEED;
    const raw = window.localStorage.getItem(V2_POOL_AI_SPEED_KEY);
    if (!raw) return DEFAULT_POOL_AI_SPEED;
    const parsed = JSON.parse(raw) as { v?: unknown; sort?: unknown; area?: unknown } | null;
    const ok = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= SPEED_MIN && value <= SPEED_MAX;
    if (!parsed || parsed.v !== 1 || !ok(parsed.sort) || !ok(parsed.area)) return DEFAULT_POOL_AI_SPEED;
    return { sort: parsed.sort, area: parsed.area, measured: true };
  } catch {
    return DEFAULT_POOL_AI_SPEED;
  }
}

// After a phase of a run: move the remembered speed halfway toward what the phase measured.
export function rememberPoolAiSpeed(kind: 'sort' | 'area', units: ReadonlyArray<{ actualSeconds: number; predictedSeconds: number }>): void {
  const real = units.filter((u) => u.actualSeconds >= MIN_MEASURED_SECONDS && u.predictedSeconds > 0);
  if (real.length === 0) return;
  const measured = clampSpeed(sum(real.map((u) => u.actualSeconds)) / sum(real.map((u) => u.predictedSeconds)));
  const current = readPoolAiSpeed();
  const next = { v: 1, sort: current.sort, area: current.area };
  next[kind] = clampSpeed((current[kind] + measured) / 2);
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(V2_POOL_AI_SPEED_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked or full: the next estimate starts from the measured figures again.
  }
}

export function forgetPoolAiSpeedForTests(): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(V2_POOL_AI_SPEED_KEY);
  } catch {
    // nothing stored
  }
}

// Whether the app went to the background during a run: a phone that was put away measures its sleep, not
// its speed, so such a run is not remembered.
export function watchAppHidden(): { wasHidden: () => boolean; stop: () => void } {
  if (typeof document === 'undefined') return { wasHidden: () => false, stop: () => undefined };
  let hidden = document.visibilityState === 'hidden';
  const onChange = () => {
    if (document.visibilityState === 'hidden') hidden = true;
  };
  document.addEventListener('visibilitychange', onChange);
  return { wasHidden: () => hidden, stop: () => document.removeEventListener('visibilitychange', onChange) };
}
